import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { getFormValues, change, formValues } from 'redux-form';
import classNames from 'classnames';

import { CLIENT_ALLOWANCES_LIST } from 'src/constants/EndpointUrls';
import * as CssClassNames from 'src/constants/CssClassNames';
import { TextField, RadioField } from 'src/jbc-front/components/Form';
import { getRadioOptions } from 'src/reducers/selectOptions';
import { maxLength100 } from 'src/utils/CustomValidators';
import { ErrorsSection } from 'src/components';
import { FormSubmitBtn, CancelBtn } from 'src/buttons';
import selector from 'src/utils/Utils';
import { redirectTo, concatParamsToUrl } from 'src/utils/Http';
import { getAllowanceFormValues } from 'src/reducers/clients/allowances';
import { useTranslation } from 'react-i18next';
import { getSelectOptions } from 'src/reducers/selectOptions';

import AllowancePayment from './AllowancePayment';
import AllowanceDeduction from './AllowanceDeduction';

import styles from './AllowanceEditingForm.scss';
import { useTutorialContext } from '../../GuideTour/TutorialProvider';
import { CLIENT_ALLOWANCE_FORM } from '../../../constants/FormNames';

// Feature flags - Centralized control for temporary features
const FEATURE_FLAGS = {
  // @TODO: Enable motivation option when Diligence allowance feature is ready
  // https://donuts-vn.atlassian.net/browse/JBC-26
  SHOW_MOTIVATION_OPTION: false
};

const allowanceListUrl = (payrollRulesGroupId) => concatParamsToUrl(CLIENT_ALLOWANCES_LIST, { payrollRulesGroupId });

const AllowanceEditingForm = (props) => {
  const { getAllTutorial, currentUserEmail, setTutorialStep2 } = useTutorialContext();
  const {
    submitting,
    submit,
    pristine,
    handleSubmit,
    errors,
    allowanceTypes,
    selectedAllowanceType,
    payrollRulesGroupId,
    payrollRulesGroup,
    formulaOrderType,
    useYearEndAdjustment,
    isUsedPayment,
    selectedPaymentType,
    change,
    selectedMotivationStep,
    selectedProbationStep,
    paymentTypesOptions,
    paymentMonths,
    selectPaymentMonth,
    selectedEmployeePaymentMonthId,
  } = props;
  // 基本給か通勤手当のいずれか
  const isBaseSalaryOrCommuting = formulaOrderType === 'base_salary' || formulaOrderType === 'commuting';
  // 支給・控除の変更が可能かどうか
  const allowanceTypeDisabled = isBaseSalaryOrCommuting || useYearEndAdjustment || isUsedPayment;
  // 支給項目
  const isPaymentAllowance = selectedAllowanceType === 'payment';
  // 控除項目
  const isDeductionAllowance = selectedAllowanceType === 'deduction';
  // 年末調整精算用の控除項目
  const isYearEndAdjustment = isDeductionAllowance && useYearEndAdjustment;
  const { t } = useTranslation();

  // Filter payment types based on feature flags
  const getFilteredPaymentTypesOptions = () => {
    if (FEATURE_FLAGS.SHOW_MOTIVATION_OPTION) {
      return paymentTypesOptions;
    }
    return paymentTypesOptions.filter(option => option.value !== 'motivation');
  };

  const filteredPaymentTypesOptions = getFilteredPaymentTypesOptions();

  const onClickCancel = () => {
    if (getAllTutorial(currentUserEmail).tutorialStep2) {
      const { run } = getAllTutorial(currentUserEmail).tutorialStep2;
      if (run) {
        setTutorialStep2({ run: false, stepIndex: 0, tourCompleted: true, type: 'next' });
      }
    }
    redirectTo(allowanceListUrl(payrollRulesGroupId));
  };

  useEffect(() => {
    if (selectedPaymentType === 'motivation') {
      change(CLIENT_ALLOWANCE_FORM, 'allowanceAmountType', null);
    }

    if (selectedMotivationStep && selectedMotivationStep.length === 0) {
      change(CLIENT_ALLOWANCE_FORM, 'motivationStep', [{ fromMonth: '1', toMonth: '', bonusAmount: '' }]);
    }

    if (selectedProbationStep && selectedProbationStep.length === 0) {
      change(CLIENT_ALLOWANCE_FORM, 'probationStep', [{ fromMonth: '1', toMonth: '', bonusAmount: '' }]);
    }
  }, [selectedPaymentType]);

  useEffect(() => {
    if (!selectPaymentMonth && selectedEmployeePaymentMonthId) {
      const getSelectedPaymentMonthValue = paymentMonths.find(
        (month) => month.paymentMonthId === selectedEmployeePaymentMonthId
      );
      change(CLIENT_ALLOWANCE_FORM, 'employeePaymentMonth', getSelectedPaymentMonthValue.value);
    }
  }, []);

  return (
    <div>
      <div className={styles.error}>
        <ErrorsSection errors={errors} />
      </div>
      <form className={classNames(CssClassNames.FORM, CssClassNames.EMPLOYEE_FORM)} onSubmit={handleSubmit}>
        <div className={styles.section}>
          <RadioField
            label={t('features.ClientAllowances.components.AllowanceEditingForm.provision_deduction')}
            name="allowanceType"
            required
            options={allowanceTypes}
            disabled={allowanceTypeDisabled || formulaOrderType === 'provident_fund'}
          />

          <TextField
            label={t('features.ClientAllowances.components.AllowanceEditingForm.item_name')}
            name="name"
            note={
              <div>
                {t('features.ClientAllowances.components.AllowanceEditingForm.The_name_shown_in_the_payslip')}
                {t(
                  'features.ClientAllowances.components.AllowanceEditingForm.Renaming_an_item_will_change_the_name_of_the_item_in_the_confirmed_salary_and_also_in_the_payslip_payroll_and_provision_deduction_list'
                )}
                {t(
                  'features.ClientAllowances.components.AllowanceEditingForm.Also_the_data_calculated_once_under_the_previous_item_name_will_be_calculated_under_the_item_name_after_the_change'
                )}
              </div>
            }
            required
            maxLength="100"
            validate={maxLength100}
            disabled={formulaOrderType === 'commuting' || formulaOrderType === 'provident_fund'}
          />

          {isPaymentAllowance && (
            <RadioField
              label={t('features.ClientAllowances.components.AllowanceEditingForm.type_of_payment')}
              name={'paymentType'}
              required
              options={filteredPaymentTypesOptions}
              disabled={formulaOrderType === 'base_salary'}
            />
          )}

          {isPaymentAllowance && (
            <AllowancePayment
              isBaseSalaryOrCommuting={isBaseSalaryOrCommuting}
              isYearEndAdjustment={isYearEndAdjustment}
              payrollRulesGroup={payrollRulesGroup}
            />
          )}
          {isDeductionAllowance && (
            <AllowanceDeduction
              isBaseSalaryOrCommuting={isBaseSalaryOrCommuting}
              isYearEndAdjustment={isYearEndAdjustment}
              payrollRulesGroup={payrollRulesGroup}
            />
          )}
        </div>
        <div className="u-ta-c">
          <CancelBtn onClick={onClickCancel} className="u-mr20" />
          <FormSubmitBtn
            text={t('features.ClientAllowances.components.AllowanceEditingForm.Save')}
            {...{ submitting, submit, pristine }}
            submit={() => submit({ ...formValues, paymentMonths })}
          />
        </div>
      </form>
    </div>
  );
};

const mapFormDispatchToProps = {
  change,
};

export default connect(
  (state) => ({
    allowanceTypes: getRadioOptions(state, 'allowanceTypes'),
    selectedPaymentType: selector(getAllowanceFormValues(state), 'paymentType'),
    selectedAllowanceType: selector(getAllowanceFormValues(state), 'allowanceType'),
    payrollRulesGroupId: selector(getAllowanceFormValues(state), 'clientPayrollRulesGroupId'),
    formulaOrderType: selector(getAllowanceFormValues(state), 'formulaOrderType'),
    useYearEndAdjustment: selector(getAllowanceFormValues(state), 'useYearEndAdjustment'),
    formValues: getFormValues(CLIENT_ALLOWANCE_FORM)(state),
    selectedMotivationStep: selector(getAllowanceFormValues(state), 'motivationStep'),
    selectedProbationStep: selector(getAllowanceFormValues(state), 'probationStep'),
    selectedMotivationBreakType: selector(getAllowanceFormValues(state), 'motivationBreakType'),
    paymentTypesOptions: getRadioOptions(state, 'paymentTypes'),
    paymentMonths: getSelectOptions(state, 'employeePaymentMonths'),
    selectPaymentMonth: selector(getAllowanceFormValues(state), 'employeePaymentMonth'),
    selectedEmployeePaymentMonthId: selector(getAllowanceFormValues(state), 'employeePaymentMonthId'),
  }),
  mapFormDispatchToProps
)(AllowanceEditingForm);
